import { css } from '@emotion/react';
import { useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { FlowEnabled, GrowthbookProvider, Navigation } from 'common';
import { useEffect } from 'react';
import { createBrowserRouter, createRoutesFromElements, Outlet, Route, RouterProvider } from 'react-router-dom';
import { MaintenanceModeKey } from 'shared';

import { RootBoundary } from './components/RootBoundary';
import { appConfig, getNavLinks, Path } from './domain/constants';
import { persistOptions, queryClient } from './domain/queryClient';
import { BookingStatus, useBookingStore } from './domain/store';
import { useInitiateBooking, useRouteValidator } from './hooks/appHooks';

const styles = {
  page: css`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  `,
};

function Root() {
  const isInitiated = useInitiateBooking();
  return isInitiated ? <Page /> : null;
}

function Page() {
  const queryClient = useQueryClient();
  const state = useBookingStore();

  useRouteValidator();

  useEffect(() => {
    // Cleanup before unload
    const handleBeforeUnload = () => {
      if (status === BookingStatus.SUCCESS || status === BookingStatus.ERROR) {
        queryClient.clear();
        state.clear();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  if (!state.booking) {
    return null;
  }

  const { status, error, signedInUser, pos } = state.booking;

  if (status === BookingStatus.ERROR) {
    throw error;
  }

  return (
    <GrowthbookProvider
      maintenanceModeKey={MaintenanceModeKey.Award}
      environment={appConfig.ENVIRONMENT}
      attributes={{ ebNumber: signedInUser?.euroBonusNumber }}
    >
      <div css={styles.page}>
        <FlowEnabled>
          <Navigation
            navLinks={getNavLinks()}
            profileLogoData={{ pos, ...signedInUser }}
            disableNavigationInLastTab={false}
          />
          <Outlet />
        </FlowEnabled>
      </div>
    </GrowthbookProvider>
  );
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<Root />} errorElement={<RootBoundary />}>
      <Route path="*" lazy={() => import('./routes/flights')} />
      <Route path={Path.TRAVELERS} lazy={() => import('./routes/travelers')} />
      <Route path={Path.REVIEW} lazy={() => import('./routes/review')} />
    </Route>
  )
);

export function App() {
  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <RouterProvider router={router} />
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  );
}
